import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchPatientProviderAssignments } from '../../../../services/admin/patient.service';
import { fetchUsers } from '../../../../services/admin/user.service';
import { useAuth } from '../../../../context/authcontext';
import type { StaffDirectory, StaffMember } from '../StaffDirectoryContext';

const fullName = (first?: string | null, last?: string | null): string =>
  [first, last].map((s) => (s || '').trim()).filter(Boolean).join(' ');

/**
 * Phase 18 M5 / F21: staff names and credentials for chart entries.
 * Care team first (GET /patientmr/{id}/providers), plus the signed-in user; tenant users
 * (GET /users) only when asked for, since not every role may list them.
 */
export const useStaffDirectory = (patientId: string | undefined): StaffDirectory => {
  const { user } = useAuth();
  const [careTeam, setCareTeam] = useState<StaffMember[]>([]);
  const [tenantStaff, setTenantStaff] = useState<StaffMember[]>([]);
  const tenantLoaded = useRef<Promise<StaffMember[]> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!patientId) {
      setCareTeam([]);
      return undefined;
    }
    fetchPatientProviderAssignments(patientId)
      .then((assignments) => {
        if (cancelled) return;
        const members: StaffMember[] = [];
        const seen = new Set<string>();
        for (const a of assignments) {
          const userId = a.userId || a.provider?.userId;
          if (!userId || seen.has(userId)) continue;
          seen.add(userId);
          members.push({
            userId,
            name: fullName(a.provider?.firstName, a.provider?.lastName) || 'Staff member',
            credentials: a.provider?.discipline || null,
            // Former members still resolve as authors of older entries
            active: a.status !== 'inactive',
          });
        }
        setCareTeam(members);
      })
      .catch(() => {
        if (!cancelled) setCareTeam([]);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const self = useMemo<StaffMember | null>(() => (user?.id
    ? { userId: user.id, name: fullName(user.firstName, user.lastName) || user.email || 'You', credentials: user.providerType || null }
    : null), [user?.id, user?.firstName, user?.lastName, user?.email, user?.providerType]);

  const byId = useMemo(() => {
    const map = new Map<string, StaffMember>();
    for (const m of tenantStaff) map.set(m.userId, m);
    for (const m of careTeam) map.set(m.userId, m);
    if (self) map.set(self.userId, self);
    return map;
  }, [careTeam, tenantStaff, self]);

  const resolve = useCallback((userId: string | null | undefined) => (userId ? byId.get(String(userId)) : undefined), [byId]);

  const loadTenantStaff = useCallback(() => {
    if (!tenantLoaded.current) {
      tenantLoaded.current = fetchUsers(1, 200, undefined, 'ACTIVE')
        .then(({ users }) => {
          const staff = users
            .filter((u) => u.id && u.isProvider)
            .map((u) => ({ userId: u.id, name: fullName(u.firstName, u.lastName) || 'Staff member', credentials: u.providerType || null }));
          setTenantStaff(staff);
          return staff;
        })
        .catch(() => [] as StaffMember[]);
    }
    return tenantLoaded.current;
  }, []);

  const careTeamWithSelf = useMemo(() => {
    const active = careTeam.filter((m) => m.active !== false);
    return self && !active.some((m) => m.userId === self.userId) ? [...active, self] : active;
  }, [careTeam, self]);

  return useMemo(() => ({ careTeam: careTeamWithSelf, tenantStaff, resolve, loadTenantStaff }), [careTeamWithSelf, tenantStaff, resolve, loadTenantStaff]);
};
